import { useMemo, useState, type ReactNode } from "react";
import { ChevronLeft } from "lucide-react";
import type { QuestionnaireSummary, QuestionnaireSummaryCta } from "@ferrivia/api-contract";
import { useFormatters, useTranslation } from "@ferrivia/i18n";
import { Text } from "@ferrivia/ui";
import { BookingSheet } from "../screens/InfusionScreen/InfusionSteps/BookingSheet";
import { LabsPart } from "./LabsPart";
import { StickyCta } from "./NextSteps";
import { toAnswerMap } from "./answerMap";
import { PartHeading } from "./PartHeading";
import { supplementsHaveContent, visibleSections } from "./partVisibility";
import { SupplementsPart } from "./SupplementsPart";
import { SymptomsPart } from "./SymptomsPart";
import { TaustatiedotPart } from "./TaustatiedotPart";
import s from "./SummaryReader.module.css";

export interface SummaryReaderProps {
  summary: QuestionnaireSummary;
  path: QuestionnaireSummaryCta | null;
  container: HTMLElement | null;
  onClose: () => void;
}

interface Part {
  id: "labs" | "symptoms" | "background" | "supplements";
  title: string;
  content: ReactNode;
}

export function SummaryReader({ summary, path, container, onClose }: SummaryReaderProps) {
  const { t } = useTranslation("client");
  const { date, time } = useFormatters();
  const [bookingOpen, setBookingOpen] = useState(false);

  const submittedAt = new Date(summary.submittedAt);
  const { quick, full } = summary.checkins;

  const answers = useMemo(() => toAnswerMap(summary.answers), [summary.answers]);

  const parts: Part[] = [];
  if (summary.labResults.length > 0) {
    parts.push({
      id: "labs",
      title: t("summary.parts.labs", "Laboratoriotulokset ja mittaushistoria"),
      content: <LabsPart labResults={summary.labResults} container={container} />,
    });
  }
  if (quick.latest || full.latest) {
    parts.push({
      id: "symptoms",
      title: t("summary.parts.symptoms", "Oireet"),
      content: <SymptomsPart quick={quick} full={full} />,
    });
  }
  if (visibleSections(answers).length > 0) {
    parts.push({
      id: "background",
      title: t("summary.parts.background", "Taustatiedot"),
      content: <TaustatiedotPart answers={answers} />,
    });
  }
  if (supplementsHaveContent(answers, summary.items)) {
    parts.push({
      id: "supplements",
      title: t("summary.parts.supplements", "Ravintolisät ja rautainfuusiot"),
      content: <SupplementsPart answers={answers} items={summary.items} />,
    });
  }

  const stamp = t("summary.stamp", "Lähetetty {{date}} klo {{time}}", {
    date: date.format(submittedAt),
    time: time.format(submittedAt),
  });

  function insideLabel(id: Part["id"]): string {
    switch (id) {
      case "labs":
        return t("summary.hero.inside.labs", "Laboratoriotulokset");
      case "symptoms":
        return t("summary.hero.inside.symptoms", "Oireet");
      case "background":
        return t("summary.hero.inside.background", "Taustatiedot");
      case "supplements":
        return t("summary.hero.inside.supplements", "Ravintolisät");
    }
  }

  return (
    <>
      <div className={s.header}>
        <button type="button" className={s.back} onClick={onClose} aria-label={t("summary.back", "Takaisin")}>
          <ChevronLeft aria-hidden size={24} />
        </button>
      </div>

      <div className={s.scroll}>
        <div className={s.content}>
          <div className={s.hero}>
            <Text as="h1" size="2xl" weight="bold" className={s.title}>
              {t("summary.title", "Henkilökohtainen rautaraporttisi")}
            </Text>
            <Text as="p" size="sm" className={s.stamp}>
              {stamp}
            </Text>
            <Text as="p" size="md" className={s.heroBody}>
              {t(
                "summary.hero.body",
                "Kaikki kertomasi yhdessä paikassa: laboratoriotuloksesi, oireesi ja taustatietosi. Lääkäri käy sen läpi ennen tapaamistasi.",
              )}
            </Text>
            {parts.length > 0 && (
              <ul className={s.inside} aria-label={t("summary.hero.insideLabel", "Raportin sisältö")}>
                {parts.map((part) => (
                  <li key={part.id} className={s.insideItem}>
                    {insideLabel(part.id)}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {parts.map((part, index) => (
            <section key={part.id} className={s.part}>
              <PartHeading number={index + 1} title={part.title} />
              {part.content}
            </section>
          ))}

        </div>
      </div>

      {path && <StickyCta path={path} onOpenBooking={() => setBookingOpen(true)} />}
      {path === "infusion" && <BookingSheet open={bookingOpen} onOpenChange={setBookingOpen} container={container} />}
    </>
  );
}
